import { renderRoutes } from 'react-router-config';
import hoistStatics from 'hoist-non-react-statics';
import React, { useState, useEffect } from 'react';
import qs from 'querystring';
import { RouteComponentProps, RouteProps } from 'react-router';
import { useStore } from 'react-redux';

type RouteConfig<T = { [key: string]: any }> = RouteProps & { routes?: RouteConfig[] } & {
  props?: T;
};

export type RouteHocProps<T = { [key: string]: any }> = {
  children: React.ReactNode;
  route: RouteConfig<T>;
  loading: boolean;
} & RouteComponentProps;

export const RouteWrapper = WrappedComponent => {
  const Route: React.FC<RouteHocProps> = props => {
    // eslint-disable-next-line no-undef
    const [loading, setLoading] = useState(!SERVER && !!WrappedComponent.needs);
    const store = useStore();

    useEffect(() => {
      async function resolveNeeds() {
        const timer = setTimeout(() => setLoading(true), 500);
        const query = qs.parse(props.location.search.replace('?', '')) || {};
        await WrappedComponent.needs(props.match, store, query);
        clearTimeout(timer);
        setLoading(false);
      }
      if (WrappedComponent.needs) {
        resolveNeeds();
      }
    }, [JSON.stringify(props.match.params), props.location.search, store]);

    return (
      <WrappedComponent loading={loading} {...props}>
        {renderRoutes(props.route.routes)}
      </WrappedComponent>
    );
  };

  const withRoute = hoistStatics(Route, WrappedComponent);
  withRoute.wrappedComponent = WrappedComponent;
  withRoute.displayName = `withRoute(${WrappedComponent.displayName || WrappedComponent.name})`;
  return withRoute;
};

const MULTISLASH = /(\/+)/g;
const dedupeSlashes = str => str.replace(MULTISLASH, '/');
const buildPath = (base, leaf) => dedupeSlashes(`${base}/${leaf}`);

const wrapRoutes = (config: RouteConfig[], basePath: string = '/') => {
  return config.map(({ component, path, routes, props = {}, ...rest }) => ({
    path: buildPath(basePath, path),
    props,
    component: RouteWrapper(component),
    routes: routes ? wrapRoutes(routes, buildPath(basePath, path)) : undefined,
    ...rest,
  }));
};

export default wrapRoutes;
